import React, { useState } from 'react';
import { format } from 'date-fns';
import {
  User,
  Bot,
  Code,
  Table,
  FileText,
  AlertCircle,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Download,
  Play,
  BookOpen,
  RefreshCw,
  Loader2,
  BarChart3,
  LineChart,
  PieChart as PieChartIcon,
  LayoutGrid,
} from 'lucide-react';
import { AddToDashboardDialog } from '@/components/Analytics/AddToDashboardDialog';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import sql from 'react-syntax-highlighter/dist/esm/languages/hljs/sql';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import ReactMarkdown from 'react-markdown';
import { ConversationMessage, MessageType } from '@/types';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table as UITable, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { ChartRenderer } from '@/components/Charts/ChartRenderer';
import { detectChartType, ChartType } from '@/utils/chartDetection';


SyntaxHighlighter.registerLanguage('sql', sql);

interface MessageBubbleProps {
  message: ConversationMessage;
  db_connection_id?: string;
  onExecuteSQL?: (sqlGenerationId: string) => void;
  onExportResults?: (sqlGenerationId: string) => void;
  onRetryQuery?: (query: string) => void;
  isExecuting?: boolean;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  db_connection_id,
  onExecuteSQL,
  onExportResults,
  onRetryQuery,
  isExecuting = false
}) => {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'query' | 'result' | 'explanation'>('query');
  const [viewMode, setViewMode] = useState<'table' | ChartType>('table');

  // Debug logging for token display
  React.useEffect(() => {
    if (message.message_type === 'SQL') {
      console.log('MessageBubble SQL message received:', {
        messageId: message.id,
        messageType: message.message_type,
        metadata: message.metadata,
        tokensUsed: message.metadata?.tokens_used,
        confidenceScore: message.metadata?.confidence_score
      });
    }
  }, [message]);

  const isUser = message.role === 'USER';
  const isError = message.role === 'ERROR' || message.message_type === MessageType.ERROR;
  const isGenerating = message.metadata?.isGenerating === true;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const renderIcon = () => {
    if (isUser) return <User className="h-5 w-5" />;
    if (isError) return <AlertCircle className="h-5 w-5 text-red-500" />;

    switch (message.message_type) {
      case MessageType.SQL:
        return <Code className="h-5 w-5 text-blue-500" />;
      case MessageType.RESULT:
        return <Table className="h-5 w-5 text-green-500" />;
      case MessageType.EXPLANATION:
        return <FileText className="h-5 w-5 text-purple-500" />;
      default:
        return <Bot className="h-5 w-5" />;
    }
  };

  const renderSQLContent = () => {
    if (isGenerating) {
      return (
        <div className="flex items-center gap-2 p-4 bg-muted/50 rounded-lg">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm text-muted-foreground">Generating SQL query...</span>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Badge variant="secondary" className="gap-1">
            <Code className="h-3 w-3" />
            SQL Query
          </Badge>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => copyToClipboard(message.content)}
            >
              {copied ? (
                <Check className="h-4 w-4" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
            {message.sql_generation_id && onExecuteSQL && !message.query_result && (
              <Button
                size="sm"
                variant="default"
                onClick={() => onExecuteSQL(message.sql_generation_id!)}
                disabled={isExecuting}
              >
                <Play className="mr-1 h-4 w-4" />
                {isExecuting ? 'Executing...' : 'Execute'}
              </Button>
            )}
          </div>
        </div>
        {/* Dark mode container for SQL code */}
        <div className="rounded-lg overflow-hidden bg-[#1e1e1e] border border-[#2d2d2d]">
          <SyntaxHighlighter
            language="sql"
            style={docco}
            customStyle={{
              padding: '1rem',
              fontSize: '14px',
              borderRadius: '0.5rem',
              background: '#1e1e1e',
              color: '#d4d4d4',
            }}
          >
            {message.content}
          </SyntaxHighlighter>
        </div>
        {message.error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <AlertCircle className="h-4 w-4 text-red-500 mt-0.5" />
            <p className="text-sm text-red-600 dark:text-red-400">{message.error}</p>
          </div>
        )}
        {/* Token usage and metadata display */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-4">
            {message.metadata?.confidence_score !== undefined && (
              <span>
                Confidence: {(message.metadata.confidence_score * 100).toFixed(1)}%
              </span>
            )}
            {message.metadata?.tokens_used !== undefined && (
              <span>
                Tokens: {message.metadata.tokens_used.toLocaleString()}
              </span>
            )}
          </div>
          {message.sql_generation_id && (
            <span className="font-mono text-xs opacity-50">
              ID: {message.sql_generation_id.slice(-8)}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderResultContent = (queryResult: any) => {
    const { columns, rows, row_count } = queryResult;
    const displayRows = expanded ? rows : rows.slice(0, 5);

    // Detect suggested chart types
    const chartSuggestions = detectChartType(columns, rows);
    const suggestedChart = chartSuggestions[0]?.type || 'table';

    // Initialize view mode to suggested chart if not set
    React.useEffect(() => {
      if (viewMode === 'table' && suggestedChart !== 'table' && rows.length > 0) {
        // Auto-select best chart for first render
        setViewMode(suggestedChart);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggestedChart, rows.length]);

    const getChartIcon = (type: ChartType) => {
      switch (type) {
        case 'bar':
          return <BarChart3 className="h-3 w-3" />;
        case 'line':
          return <LineChart className="h-3 w-3" />;
        case 'pie':
          return <PieChartIcon className="h-3 w-3" />;
        case 'area':
          return <LineChart className="h-3 w-3" />;
        default:
          return <Table className="h-3 w-3" />;
      }
    };

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Badge variant="secondary" className="gap-1">
            <Table className="h-3 w-3" />
            Query Results ({row_count} rows)
          </Badge>
          <div className="flex gap-2">
            {/* View mode selector */}
            <div className="flex items-center gap-1 mr-2">
              {['table', 'bar', 'line', 'pie', 'area'].map((type) => {
                const chartType = type as 'table' | ChartType;
                const suggestion = chartSuggestions.find(s => s.type === chartType);
                const isAvailable = suggestion || type === 'table';

                return (
                  <Button
                    key={type}
                    size="sm"
                    variant={viewMode === type ? 'default' : 'ghost'}
                    onClick={() => setViewMode(chartType)}
                    disabled={!isAvailable}
                    title={suggestion?.reason || 'Not suitable for this data'}
                    className="px-2 py-1"
                  >
                    {getChartIcon(chartType)}
                  </Button>
                );
              })}
            </div>

            {rows.length > 5 && viewMode === 'table' && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? (
                  <>
                    <ChevronUp className="mr-1 h-4 w-4" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="mr-1 h-4 w-4" />
                    Show All
                  </>
                )}
              </Button>
            )}
            {message.sql_generation_id && onExportResults && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onExportResults(message.sql_generation_id!)}
              >
                <Download className="mr-1 h-4 w-4" />
                Export CSV
              </Button>
            )}
            {message.sql_generation_id && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleAddToDashboard}
                title="Pin to Dashboard"
              >
                <LayoutGrid className="mr-1 h-4 w-4" />
                Pin
              </Button>
            )}
          </div>
        </div>

        {/* Render chart or table based on view mode */}
        {viewMode !== 'table' ? (
          <div className="h-[400px] w-full mt-4">
            <ChartRenderer
              chartType={viewMode}
              columns={columns}
              rows={rows}
              rowCount={row_count}
            />
          </div>
        ) : (
          <>
            {/* Dark mode container for data table */}
            <div className="w-full overflow-hidden rounded-lg border border-[#2d2d2d] bg-[#1e1e1e] text-[#d4d4d4]">
              <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
                <UITable>
                  <TableHeader className="bg-[#252526]">
                    <TableRow className="border-[#2d2d2d] hover:bg-[#2a2a2b]">
                      {columns.map((col: string, idx: number) => (
                        <TableHead key={idx} className="whitespace-nowrap text-[#d4d4d4] font-medium">{col}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayRows.map((row: any[], rowIdx: number) => (
                      <TableRow key={rowIdx} className="border-[#2d2d2d] hover:bg-[#2a2a2b]">
                        {row.map((cell, cellIdx) => (
                          <TableCell key={cellIdx} className="max-w-[25%] break-words text-sm overflow-wrap-anywhere text-[#d4d4d4]">
                            {cell === null ? (
                              <span className="text-[#858585] italic">NULL</span>
                            ) : (
                              <span title={String(cell)}>{String(cell)}</span>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </UITable>
              </div>
            </div>
            {!expanded && rows.length > 5 && (
              <p className="text-sm text-muted-foreground text-center">
                Showing 5 of {row_count} rows
              </p>
            )}
          </>
        )}
      </div>
    );
  };


  const renderContent = () => {
    // For SQL messages with results or explanation, show tabs
    if (message.message_type === MessageType.SQL && (message.query_result || message.explanation)) {
      return (
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="w-full">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="query" className="gap-1">
              <Code className="h-3 w-3" />
              Query
            </TabsTrigger>
            {message.query_result && (
              <TabsTrigger value="result" className="gap-1">
                <Table className="h-3 w-3" />
                Result ({message.query_result.row_count})
              </TabsTrigger>
            )}
            {message.explanation && (
              <TabsTrigger value="explanation" className="gap-1">
                <BookOpen className="h-3 w-3" />
                Explanation
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="query" className="mt-4">
            {renderSQLContent()}
          </TabsContent>

          {message.query_result && (
            <TabsContent value="result" className="mt-4">
              {renderResultContent(message.query_result)}
            </TabsContent>
          )}

          {message.explanation && (
            <TabsContent value="explanation" className="mt-4">
              <div className="prose prose-sm max-w-none dark:prose-invert">
                <ReactMarkdown>{message.explanation}</ReactMarkdown>
              </div>
            </TabsContent>
          )}
        </Tabs>
      );
    }

    switch (message.message_type) {
      case MessageType.SQL:
        return renderSQLContent();

      case MessageType.RESULT:
        if (!message.query_result) return null;
        return renderResultContent(message.query_result);

      case MessageType.EXPLANATION:
        return (
          <div className="prose prose-sm max-w-none dark:prose-invert">
            <ReactMarkdown>{message.content}</ReactMarkdown>
          </div>
        );

      case MessageType.ERROR:
        return (
          <div className="space-y-2">
            <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <AlertCircle className="h-4 w-4 text-red-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-600 dark:text-red-400">{message.content}</p>
              </div>
            </div>
            {onRetryQuery && message.metadata && message.metadata.original_query && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onRetryQuery(message.metadata?.original_query)}
                className="w-full"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry Query
              </Button>
            )}
          </div>
        );

      default:
        return <p className="whitespace-pre-wrap">{message.content}</p>;
    }
  };



  const [showAddToDashboard, setShowAddToDashboard] = useState(false);
  const [dashboardWidgetConfig, setDashboardWidgetConfig] = useState<any>(null);

  const handleAddToDashboard = () => {
    if (!message.query_result) return;

    // Suggest title
    const title = message.metadata?.title || `Query Result ${format(new Date(), 'MMM d, HH:mm')}`;

    // Determine chart type and config
    // If table view, we default to table. If chart view, we capture that.
    const type = viewMode === 'table' ? 'table' : 'chart';

    const vizConfig = viewMode !== 'table' ? {
      chart_type: viewMode,
      x_axis: message.query_result.columns[0], // heuristic
      y_axis: message.query_result.columns.slice(1).filter(c =>
        typeof message.query_result?.rows[0]?.[message.query_result.columns.indexOf(c)] === 'number'
      )
    } : undefined;

    setDashboardWidgetConfig({
      title,
      type,
      db_connection_id,
      sql_query: message.content, // Assuming SQL message content is the query
      query_id: message.sql_generation_id,
      layout: { x: 0, y: 0, w: 6, h: 4 },
      viz_config: vizConfig
    });

    setShowAddToDashboard(true);
  };

  return (
    <>
      <div
        className={cn(
          "flex gap-3 group",
          isUser ? "flex-row-reverse" : "flex-row"
        )}
      >
        <div
          className={cn(
            "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
            isUser
              ? "bg-primary text-primary-foreground"
              : isError
                ? "bg-red-100 dark:bg-red-900/20"
                : "bg-muted"
          )}
        >
          {renderIcon()}
        </div>
        <div
          className={cn(
            "flex-1 min-w-0 space-y-2",
            isUser ? "items-end" : "items-start"
          )}
        >
          <Card
            className={cn(
              "max-w-[95%] min-w-0 overflow-hidden p-4 bg-white border-[#e6e6ea] shadow-sm",
              isUser ? "ml-auto" : "mr-auto",
              isError && "border-red-200 bg-red-50"
            )}
          >
            {renderContent()}

            {/* Inject Pin Button into renderResultContent logic roughly via conditional? 
                Actually hard to inject into helper function without prop drilling or lifting.
                Better to refactor renderResultContent? 
                Or just access the buttons section inside renderResultContent.
                
                Wait, renderResultContent is defined INSIDE component. I can modify it directly.
                BUT, I am replacing the END of the component here.
                I need to MODIFY renderResultContent to include the button.
            */}
          </Card>
          <p className={cn(
            "text-xs text-[#6b6b6b] px-2",
            isUser ? "text-right" : "text-left"
          )}>
            {format(new Date(message.created_at), 'HH:mm')}
          </p>
        </div>
      </div>

      {dashboardWidgetConfig && (
        <AddToDashboardDialog
          open={showAddToDashboard}
          onOpenChange={setShowAddToDashboard}
          widgetConfig={dashboardWidgetConfig}
        />
      )}
    </>
  );
};

export default MessageBubble;